import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import { resolveProviderLogoUrl } from '../utils/resolveProviderLogoUrl';

export type ProviderAddressFields = {
  name: string;
  address?: string;
  city?: string;
  region?: string;
  country?: string;
  postal_code?: string;
  latitude?: number;
  longitude?: number;
};

interface NominatimAddress {
  house_number?: string;
  road?: string;
  pedestrian?: string;
  neighbourhood?: string;
  suburb?: string;
  city?: string;
  town?: string;
  village?: string;
  municipality?: string;
  county?: string;
  state?: string;
  postcode?: string;
  country?: string;
}

interface NominatimResult {
  place_id: number;
  lat: string;
  lon: string;
  display_name: string;
  address?: NominatimAddress;
}

const FR_CENTER: [number, number] = [46.603354, 1.888334];
const DEFAULT_ZOOM_NO_POS = 6;
const DEFAULT_ZOOM_POS = 15;

function createProviderLogoIcon(logoUrl: string | undefined): L.DivIcon {
  if (logoUrl) {
    const safe = logoUrl.replace(/"/g, '&quot;').replace(/</g, '');
    return L.divIcon({
      className: 'provider-logo-marker',
      html: `<div style="width:44px;height:44px;border-radius:50%;border:3px solid #1565c0;box-shadow:0 2px 10px rgba(0,0,0,0.35);overflow:hidden;background:#fff;"><img src="${safe}" alt="" style="width:100%;height:100%;object-fit:cover;" /></div>`,
      iconSize: [44, 44],
      iconAnchor: [22, 22],
    });
  }
  return L.divIcon({
    className: 'provider-logo-marker',
    html:
      '<div style="width:40px;height:40px;border-radius:50%;background:#1565c0;color:#fff;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.35);font-weight:700;font-size:18px;">S</div>',
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
}

function parseNominatimToAddress(
  r: NominatimResult,
  previousName: string
): ProviderAddressFields {
  const a = r.address || {};
  const streetParts = [a.house_number, a.road || a.pedestrian].filter(Boolean);
  const street = streetParts.join(' ').trim();
  const city =
    a.city || a.town || a.village || a.municipality || a.suburb || a.neighbourhood || '';
  const region = a.state || a.county || '';
  const country = a.country || 'France';
  const postal_code = a.postcode || '';

  const lat = parseFloat(r.lat);
  const lon = parseFloat(r.lon);
  return {
    name: previousName || street || city || r.display_name.split(',')[0]?.trim() || '',
    address: street || undefined,
    city: city || undefined,
    region: region || undefined,
    country,
    postal_code: postal_code || undefined,
    latitude: Number.isFinite(lat) ? lat : undefined,
    longitude: Number.isFinite(lon) ? lon : undefined,
  };
}

async function searchNominatim(query: string): Promise<NominatimResult[]> {
  const q = query.trim();
  if (q.length < 3) return [];
  const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=8&q=${encodeURIComponent(
    q
  )}`;
  const res = await fetch(url, {
    headers: {
      Accept: 'application/json',
      'Accept-Language': 'fr',
    },
  });
  if (!res.ok) return [];
  return res.json();
}

function MapClickHandler({
  onPick,
}: {
  onPick: (lat: number, lng: number) => void;
}) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function MapViewSync({ lat, lng, hasPosition }: { lat: number; lng: number; hasPosition: boolean }) {
  const map = useMap();
  useEffect(() => {
    if (hasPosition && Number.isFinite(lat) && Number.isFinite(lng)) {
      map.setView([lat, lng], DEFAULT_ZOOM_POS);
    }
  }, [lat, lng, hasPosition, map]);
  return null;
}

interface ProviderAddressAutocompleteMapProps {
  value: ProviderAddressFields;
  onChange: (next: ProviderAddressFields) => void;
  logoUrl?: string | null;
  /** Erreurs par clé simple : name, address, city, etc. */
  fieldErrors?: Record<string, string>;
}

const ProviderAddressAutocompleteMap: React.FC<ProviderAddressAutocompleteMapProps> = ({
  value,
  onChange,
  logoUrl,
  fieldErrors = {},
}) => {
  const [options, setOptions] = useState<NominatimResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [addrQuery, setAddrQuery] = useState('');
  const resolvedLogo = useMemo(
    () => resolveProviderLogoUrl(logoUrl) || undefined,
    [logoUrl]
  );
  const markerIcon = useMemo(() => createProviderLogoIcon(resolvedLogo), [resolvedLogo]);

  useEffect(() => {
    const q = addrQuery.trim();
    if (q.length < 3) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await searchNominatim(q);
        if (!cancelled) setOptions(results);
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [addrQuery]);

  const hasPosition =
    typeof value.latitude === 'number' &&
    typeof value.longitude === 'number' &&
    Number.isFinite(value.latitude) &&
    Number.isFinite(value.longitude);

  const center: [number, number] = hasPosition
    ? [value.latitude as number, value.longitude as number]
    : FR_CENTER;
  const zoom = hasPosition ? DEFAULT_ZOOM_POS : DEFAULT_ZOOM_NO_POS;

  const handleMapPick = (lat: number, lng: number) => {
    onChange({
      ...value,
      latitude: lat,
      longitude: lng,
    });
  };

  const patch = (field: keyof ProviderAddressFields, v: string | number | undefined) => {
    onChange({
      ...value,
      [field]: v,
    });
  };

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Recherche d&apos;adresse (OpenStreetMap) — la carte se met à jour ; vous pouvez aussi cliquer
        sur la carte pour placer le repère.
      </Typography>

      <Autocomplete
        fullWidth
        options={options}
        loading={loading}
        filterOptions={(x) => x}
        getOptionLabel={(o) => (typeof o === 'string' ? o : o.display_name)}
        isOptionEqualToValue={(a, b) => a.place_id === b.place_id}
        inputValue={inputValue}
        onInputChange={(_, v, reason) => {
          setInputValue(v);
          if (reason === 'input') setAddrQuery(v);
        }}
        onChange={(_, newValue) => {
          if (newValue && typeof newValue !== 'string') {
            const parsed = parseNominatimToAddress(newValue, value.name);
            onChange({ ...value, ...parsed });
            setInputValue(newValue.display_name);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Rechercher une adresse"
            placeholder="Ex. 10 rue de Rivoli Paris"
            margin="normal"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      <Box
        sx={{
          width: '100%',
          height: 280,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          mt: 2,
          mb: 2,
        }}
      >
        <MapContainer
          center={center}
          zoom={zoom}
          style={{ width: '100%', height: '100%' }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapViewSync
            lat={value.latitude ?? 0}
            lng={value.longitude ?? 0}
            hasPosition={hasPosition}
          />
          <MapClickHandler onPick={handleMapPick} />
          {hasPosition && (
            <Marker position={[value.latitude as number, value.longitude as number]} icon={markerIcon} />
          )}
        </MapContainer>
      </Box>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        Repère : logo du partenaire s&apos;il est défini, sinon marqueur par défaut.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Nom du lieu"
            value={value.name}
            onChange={(e) => patch('name', e.target.value)}
            error={!!fieldErrors.name}
            helperText={fieldErrors.name}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Rue / numéro"
            value={value.address ?? ''}
            onChange={(e) => patch('address', e.target.value || undefined)}
            error={!!fieldErrors.address}
            helperText={fieldErrors.address}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Ville"
            value={value.city ?? ''}
            onChange={(e) => patch('city', e.target.value || undefined)}
            error={!!fieldErrors.city}
            helperText={fieldErrors.city}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Région"
            value={value.region ?? ''}
            onChange={(e) => patch('region', e.target.value || undefined)}
            error={!!fieldErrors.region}
            helperText={fieldErrors.region}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Code postal"
            value={value.postal_code ?? ''}
            onChange={(e) => patch('postal_code', e.target.value || undefined)}
            error={!!fieldErrors.postal_code}
            helperText={fieldErrors.postal_code}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Pays"
            value={value.country ?? ''}
            onChange={(e) => patch('country', e.target.value || undefined)}
            error={!!fieldErrors.country}
            helperText={fieldErrors.country}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Latitude"
            value={value.latitude ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              patch('latitude', v === '' ? undefined : parseFloat(v));
            }}
            type="number"
            inputProps={{ step: 'any' }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Longitude"
            value={value.longitude ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              patch('longitude', v === '' ? undefined : parseFloat(v));
            }}
            type="number"
            inputProps={{ step: 'any' }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProviderAddressAutocompleteMap;
